import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
    SaveIcon as Save,
    PlusIcon as Add,
    DeleteIcon as Delete,
    EditIcon as Edit,
    SparklesIcon,
    ArrowLeftIcon as ArrowLeft,
    RefreshIcon as RefreshCw,
    CheckCircleIcon as CheckIcon,
    UploadIcon,
    ImageIcon,
    MessageIcon,
    ShieldCheckIcon,
    BookOpenIcon,
    SlidersIcon
} from '../components/Icons';

const getInitialPrompt = (org = {}) => {
    const stamm = org.stammName || 'Pfadfinder Stamm';
    const fullName = org.stammFullName || stamm;
    const bund = org.bundName || 'Pfadfinderbund';
    const dachverband = org.dachverband || 'Deutscher Pfadfinderverband';

    return `Du bist "Bob", der freundliche, schlaue Pfadfinder-Guide und KI-Assistent auf der Website von "${stamm}".
Deine Aufgabe ist es, Besuchern (Kindern, Eltern, Interessierten) Fragen zum Stamm, zu unseren Aktionen, Gruppenstunden, Beiträgen und zur Anmeldung zu beantworten.

### WICHTIGE FAKTEN & VERBANDSZUGEHÖRIGKEIT:
- Wir sind "${stamm}" (${fullName}).
- Unser Dachverband ist der "${dachverband}". Wir gehören dem "${bund}" an.
- WICHTIG: Wenn du über den Verband sprichst, erwähne ausschließlich den "${dachverband}" und den "${bund}".
- ⚠️ ZURÜCKHALTUNG: Erwähne unsere Verbandszugehörigkeit zum ${bund} oder ${dachverband} NUR, wenn der Benutzer explizit danach fragt. Weise NICHT ungefragt darauf hin.

### VERMEIDUNG VON HALLUZINATIONEN (STRIKTES GROUNDING):
- Beantworte Fragen zu spezifischen Details (wie Terminen, Events, E-Mail-Adressen, Telefonnummern, Personen oder Gruppenstunden-Zeiten) AUSSCHLIESSLICH auf Basis des im [Aktueller System-Kontext] oder [Aktueller öffentlicher Kontext] bereitgestellten Wissens.
- Wenn eine Information dort nicht existiert, erfinde sie NIEMALS! Sag stattdessen höflich, dass dir diese Information aktuell nicht vorliegt, und verweise den Benutzer auf das Kontaktformular oder die Stammesleitung.

### DEINE PERSÖNLICHKEIT:
- Du bist ein fröhlicher, hilfsbereiter Guide. Du sprichst freundlich, offen, bleibst dabei aber immer informativ und verständlich.
- Du antwortest auf Deutsch. Du bist per "Du" mit den Besuchern, sprichst höflich und einladend.
- Du liebst die Natur und das Pfadfinderleben.`;
};

const DEFAULT_AVATAR_STATES = [
    { key: 'bob_start', label: 'Start & Standard (FAB-Icon)', description: 'Wird auf dem schwebenden Button und beim Öffnen des Chats angezeigt.' },
    { key: 'bob_thinking', label: 'Nachdenkend (Thinking)', description: 'Wird angezeigt, während Bob die Antwort auf eine Frage formuliert.' },
    { key: 'bob_success', label: 'Glücklich / Erfolg (Success)', description: 'Wird bei erfolgreichen Aktionen oder passenden Antworten angezeigt.' },
    { key: 'bob_questionmark', label: 'Fragend (Question)', description: 'Wird angezeigt, wenn Bob Rückfragen stellt oder mehr Details benötigt.' },
    { key: 'bob_error', label: 'Fehler / Entschuldigung (Error)', description: 'Wird angezeigt, wenn eine Anfrage fehlschlägt oder keine Daten vorliegen.' }
];

const AdminBobSettings = () => {
    const { user, API_URL, getAssetUrl } = useContext(AuthContext);
    const { organization } = useSettings();
    const token = user?.token;

    const [activeTab, setActiveTab] = useState('general'); // 'general' | 'prompt' | 'access' | 'images' | 'knowledge'
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    // AI Config State
    const [config, setConfig] = useState({
        enabled: true,
        botName: 'Bob',
        firstMessage: 'Hallo! Ich bin Bob, dein Pfadfinder-Guide. 😊 Ich helfe dir gerne bei Fragen zu unserem Stamm, zu Aktionen, Gruppenstunden oder zur Anmeldung! Was möchtest du wissen?',
        bubbleText: 'Frag mich etwas über unseren Stamm!',
        quickQuestions: [
            'Wann finden die Gruppenstunden statt?',
            'Wie kann mein Kind reinschnuppern?',
            'Was kostet die Mitgliedschaft?',
            'Wo finden die Zeltlager statt?'
        ],
        systemPrompt: '',
        game404Enabled: false,
        access: {
            blog: true,
            events: true,
            groups: true,
            team: true,
            organization: true,
            knowledge: true
        },
        customImages: {
            bob_start: '',
            bob_thinking: '',
            bob_success: '',
            bob_questionmark: '',
            bob_error: ''
        },
        extraImages: [] // [ { key: 'bob_custom', name: 'Zusatzbild', url: '' } ]
    });

    // Knowledge entries state
    const [knowledgeItems, setKnowledgeItems] = useState([]);
    const [newFact, setNewFact] = useState({ title: '', category: 'Allgemein', text: '' });
    const [editingFactIndex, setEditingFactIndex] = useState(null);

    // Image Upload State
    const [uploadingKey, setUploadingKey] = useState(null);
    const [newQuickQuestion, setNewQuickQuestion] = useState('');

    const authConfig = {
        headers: { Authorization: `Bearer ${token}` }
    };

    useEffect(() => {
        const fetchAiSettings = async () => {
            setIsLoading(true);
            try {
                // Fetch dynamic AI settings
                const [aiRes, promptRes, knowledgeRes, integRes] = await Promise.allSettled([
                    axios.get(`${API_URL}/settings/ai_settings`, authConfig),
                    axios.get(`${API_URL}/settings/bob_system_prompt`, authConfig),
                    axios.get(`${API_URL}/settings/bob_knowledge`, authConfig),
                    axios.get(`${API_URL}/settings/integrations`, authConfig)
                ]);

                const aiData = aiRes.status === 'fulfilled' ? aiRes.value.data : {};
                const promptData = promptRes.status === 'fulfilled' ? promptRes.value.data : {};
                const knowledgeData = knowledgeRes.status === 'fulfilled' ? knowledgeRes.value.data : [];
                const integData = integRes.status === 'fulfilled' ? integRes.value.data : {};

                const initialPrompt = promptData.prompt || aiData.systemPrompt || getInitialPrompt(organization);

                setConfig(prev => ({
                    ...prev,
                    enabled: integData?.ai?.enabled !== false && aiData?.enabled !== false,
                    botName: aiData.botName || integData?.ai?.botName || 'Bob',
                    firstMessage: aiData.firstMessage || prev.firstMessage,
                    bubbleText: aiData.bubbleText || prev.bubbleText,
                    quickQuestions: Array.isArray(aiData.quickQuestions) && aiData.quickQuestions.length > 0 ? aiData.quickQuestions : prev.quickQuestions,
                    systemPrompt: initialPrompt,
                    game404Enabled: aiData.game404Enabled === true || integData?.ai?.game404Enabled === true,
                    access: {
                        blog: aiData.access?.blog !== false,
                        events: aiData.access?.events !== false,
                        groups: aiData.access?.groups !== false,
                        team: aiData.access?.team !== false,
                        organization: aiData.access?.organization !== false,
                        knowledge: aiData.access?.knowledge !== false
                    },
                    customImages: {
                        bob_start: aiData.customImages?.bob_start || '',
                        bob_thinking: aiData.customImages?.bob_thinking || '',
                        bob_success: aiData.customImages?.bob_success || '',
                        bob_questionmark: aiData.customImages?.bob_questionmark || '',
                        bob_error: aiData.customImages?.bob_error || ''
                    },
                    extraImages: Array.isArray(aiData.extraImages) ? aiData.extraImages : []
                }));

                if (Array.isArray(knowledgeData)) {
                    setKnowledgeItems(knowledgeData);
                } else if (Array.isArray(aiData.knowledge)) {
                    setKnowledgeItems(aiData.knowledge);
                }
            } catch (err) {
                console.error('Failed to load AI settings', err);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchAiSettings();
        }
    }, [API_URL, token]);

    const handleSaveAll = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);

        try {
            const payload = {
                ...config,
                knowledge: knowledgeItems
            };

            // 1. Save unified AI settings
            await axios.put(`${API_URL}/settings/ai_settings`, payload, authConfig);

            // 2. Also sync to bob_system_prompt & bob_knowledge for backward compatibility
            await Promise.allSettled([
                axios.put(`${API_URL}/settings/bob_system_prompt`, { prompt: config.systemPrompt }, authConfig),
                axios.put(`${API_URL}/settings/bob_knowledge`, knowledgeItems, authConfig),
                // Update integration AI enabled flag if changed
                axios.put(`${API_URL}/settings/integrations`, {
                    ai: {
                        enabled: config.enabled,
                        botName: config.botName,
                        publicChatEnabled: config.enabled,
                        dashboardAgentEnabled: config.enabled,
                        game404Enabled: config.game404Enabled
                    }
                }, authConfig)
            ]);

            // 3. Also synchronize homepage settings
            try {
                const hpRes = await axios.get(`${API_URL}/settings/homepage`, authConfig);
                if (hpRes.data) {
                    const currentModules = hpRes.data.modules || {};
                    const currentFeatures = hpRes.data.features || {};
                    if (currentModules.aiAssistant !== config.enabled || currentFeatures.bobChat !== config.enabled) {
                        await axios.put(`${API_URL}/settings/homepage`, {
                            ...hpRes.data,
                            modules: { ...currentModules, aiAssistant: config.enabled },
                            features: { ...currentFeatures, bobChat: config.enabled }
                        }, authConfig);
                    }
                }
            } catch (e) {
                console.warn('Could not sync homepage settings from BobSettings:', e);
            }

            setMessage('Alle KI- und Bot-Einstellungen erfolgreich gespeichert!');
            setTimeout(() => setMessage(''), 4000);
        } catch (err) {
            setIsError(true);
            setMessage(err.response?.data?.message || 'Fehler beim Speichern der Einstellungen.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageUpload = async (key, file, isExtra = false, extraIndex = null) => {
        if (!file) return;
        setUploadingKey(key);
        setMessage('');

        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await axios.post(`${API_URL}/files/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            const uploadedUrl = res.data.url;

            if (isExtra && extraIndex !== null) {
                setConfig(prev => {
                    const nextExtra = [...prev.extraImages];
                    nextExtra[extraIndex].url = uploadedUrl;
                    return { ...prev, extraImages: nextExtra };
                });
            } else {
                setConfig(prev => ({
                    ...prev,
                    customImages: {
                        ...prev.customImages,
                        [key]: uploadedUrl
                    }
                }));
            }

            setMessage(`Bild für "${key}" erfolgreich hochgeladen!`);
            setIsError(false);
        } catch (err) {
            setIsError(true);
            setMessage(err.response?.data?.message || 'Fehler beim Hochladen des Bildes.');
        } finally {
            setUploadingKey(null);
        }
    };

    const handleAddExtraImage = () => {
        const keyName = `custom_${Date.now()}`;
        setConfig(prev => ({
            ...prev,
            extraImages: [
                ...prev.extraImages,
                { key: keyName, name: 'Neuer Zustand', url: '' }
            ]
        }));
    };

    const handleRemoveExtraImage = (idx) => {
        setConfig(prev => ({
            ...prev,
            extraImages: prev.extraImages.filter((_, i) => i !== idx)
        }));
    };

    const handleAddQuickQuestion = () => {
        if (!newQuickQuestion.trim()) return;
        setConfig(prev => ({
            ...prev,
            quickQuestions: [...prev.quickQuestions, newQuickQuestion.trim()]
        }));
        setNewQuickQuestion('');
    };

    const handleRemoveQuickQuestion = (idx) => {
        setConfig(prev => ({
            ...prev,
            quickQuestions: prev.quickQuestions.filter((_, i) => i !== idx)
        }));
    };

    const handleAddOrUpdateFact = () => {
        if (!newFact.text.trim()) return;

        if (editingFactIndex !== null) {
            const updated = [...knowledgeItems];
            updated[editingFactIndex] = newFact;
            setKnowledgeItems(updated);
            setEditingFactIndex(null);
        } else {
            setKnowledgeItems(prev => [...prev, { ...newFact, id: Date.now().toString() }]);
        }

        setNewFact({ title: '', category: 'Allgemein', text: '' });
    };

    const handleResetPromptToDefault = () => {
        if (window.confirm('Möchtest du den System-Prompt wirklich auf die Standard-Vorlage für deinen Stamm zurücksetzen?')) {
            setConfig(prev => ({
                ...prev,
                systemPrompt: getInitialPrompt(organization)
            }));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <RefreshCw className="animate-spin text-primary" sx={{ fontSize: 36 }} />
            </div>
        );
    }

    const tabs = [
        { id: 'general', label: 'Begrüßung & Dialog', icon: <MessageIcon sx={{ fontSize: 18 }} /> },
        { id: 'prompt', label: 'Prompt & Persönlichkeit', icon: <SparklesIcon sx={{ fontSize: 18 }} /> },
        { id: 'access', label: 'Datenzugriff & Rechte', icon: <ShieldCheckIcon sx={{ fontSize: 18 }} /> },
        { id: 'images', label: 'Bilder & Maskottchen', icon: <ImageIcon sx={{ fontSize: 18 }} /> },
        { id: 'knowledge', label: 'Wissensdatenbank (FAQ)', icon: <BookOpenIcon sx={{ fontSize: 18 }} /> },
    ];

    return (
        <div className="space-y-8 animate-fade-in max-w-6xl mx-auto pb-16">
            {/* Header & Global Save */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-outlineVariant/30">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-2xl shrink-0">
                        🤖
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className="text-headline-small font-bold text-surface-on">KI-Assistent & Bot-Konfiguration</h2>
                            <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${config.enabled ? 'bg-primary/10 text-primary' : 'bg-surface-containerHighest text-surface-onVariant'}`}>
                                {config.enabled ? 'Aktiviert' : 'Deaktiviert'}
                            </span>
                        </div>
                        <p className="text-body-medium text-surface-onVariant mt-1">
                            Passe Begrüßung, Tonalität, Bilder und Datenzugriff deines virtuellen Pfadfinder-Guides an.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {message && (
                        <span className={`text-label-medium font-bold animate-fade-in ${isError ? 'text-error' : 'text-primary'}`}>
                            {isError ? '⚠️' : '✓'} {message}
                        </span>
                    )}
                    <button
                        onClick={handleSaveAll}
                        disabled={isSaving}
                        className="md3-btn-filled flex items-center gap-2 px-6 h-12 shadow-sm shrink-0"
                    >
                        {isSaving ? <RefreshCw className="animate-spin" sx={{ fontSize: 18 }} /> : <Save sx={{ fontSize: 18 }} />}
                        <span>Speichern</span>
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none border-b border-outlineVariant/20">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 rounded-2xl text-label-large font-medium transition-all shrink-0 flex items-center gap-2 ${
                            activeTab === tab.id
                                ? 'bg-primary text-primary-on shadow-sm'
                                : 'bg-surface-containerHigh/70 text-surface-on hover:bg-surface-containerHighest'
                        }`}
                    >
                        {tab.icon}
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* TAB 1: ALLGEMEIN & BEGRÜSSUNG */}
            {activeTab === 'general' && (
                <div className="space-y-6 animate-fade-in">
                    {/* Master Switch Card */}
                    <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                🔌
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">KI-Assistent aktivieren</h3>
                                <p className="text-body-small text-surface-onVariant mt-0.5">
                                    Aktiviert oder deaktiviert den Assistenten auf der gesamten Website sowie im Dashboard.
                                </p>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                                type="checkbox"
                                checked={config.enabled}
                                onChange={e => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                                className="sr-only peer"
                            />
                            <div className="w-12 h-7 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>

                    {/* 404 Game Switch Card */}
                    <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center text-xl shrink-0">
                                🧭
                            </div>
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on">Bob Text-Adventure auf 404-Seite</h3>
                                <p className="text-body-small text-surface-onVariant mt-0.5">
                                    Erlaubt Besuchern, auf fehlerhaften Links (404) das interaktive Bob Text-Adventure zu spielen. Wenn deaktiviert, wird der übersichtliche Pfadfinder-Wegweiser mit Offline-Orientierungsspiel angezeigt.
                                </p>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                                type="checkbox"
                                checked={config.game404Enabled}
                                disabled={!config.enabled}
                                onChange={e => setConfig(prev => ({ ...prev, game404Enabled: e.target.checked }))}
                                className="sr-only peer disabled:opacity-50"
                            />
                            <div className="w-12 h-7 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                        </label>
                    </div>

                    {/* Bot Profile Card */}
                    <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm space-y-6">
                        <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                            <span>💬</span> Dialog & Begrüßung
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-2">Bot-Name</label>
                                <input
                                    type="text"
                                    value={config.botName}
                                    onChange={e => setConfig(prev => ({ ...prev, botName: e.target.value }))}
                                    placeholder="z.B. Bob oder PfadiBot"
                                    className="md3-textfield-outlined w-full"
                                />
                                <p className="text-[12px] text-surface-onVariant mt-1.5 opacity-70">
                                    Der Name, mit dem sich der Bot im Chat vorstellt.
                                </p>
                            </div>

                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-2">Sprechblasen-Teaser (Homepage Button)</label>
                                <input
                                    type="text"
                                    value={config.bubbleText}
                                    onChange={e => setConfig(prev => ({ ...prev, bubbleText: e.target.value }))}
                                    placeholder="Frag mich etwas über die Pfadfinder!"
                                    className="md3-textfield-outlined w-full"
                                />
                                <p className="text-[12px] text-surface-onVariant mt-1.5 opacity-70">
                                    Erscheint nach ein paar Sekunden kurz über dem schwebenden Icon auf der Startseite.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-2">
                                Erste Willkommens-Nachricht (First Message)
                            </label>
                            <textarea
                                value={config.firstMessage}
                                onChange={e => setConfig(prev => ({ ...prev, firstMessage: e.target.value }))}
                                rows={3}
                                className="md3-textfield-outlined w-full text-sm leading-relaxed"
                                placeholder="Hallo! Ich bin dein digitaler Pfadfinder-Guide..."
                            />
                            <p className="text-[12px] text-surface-onVariant mt-1.5 opacity-70">
                                Diese Nachricht steht als erste Sprechblase im Chatfenster, sobald der Besucher auf den Bot klickt.
                            </p>
                        </div>

                        {/* Quick Action Questions */}
                        <div className="pt-4 border-t border-outlineVariant/30 space-y-3">
                            <label className="text-label-small font-bold text-surface-onVariant block">
                                Vorgeschlagene Startfragen (Quick Questions)
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {config.quickQuestions.map((q, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-containerHigh text-surface-on text-xs font-medium border border-outlineVariant/40"
                                    >
                                        <span>{q}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveQuickQuestion(idx)}
                                            className="hover:text-error transition-colors text-surface-onVariant"
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2 max-w-lg mt-2">
                                <input
                                    type="text"
                                    value={newQuickQuestion}
                                    onChange={e => setNewQuickQuestion(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddQuickQuestion())}
                                    placeholder="Neue Startfrage hinzufügen..."
                                    className="md3-textfield-outlined flex-1 text-xs"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddQuickQuestion}
                                    className="md3-btn-filled text-xs px-4"
                                >
                                    Hinzufügen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: SYSTEM-PROMPT & PERSÖNLICHKEIT */}
            {activeTab === 'prompt' && (
                <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm space-y-6 animate-fade-in">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                <SparklesIcon sx={{ fontSize: 20 }} className="text-primary" />
                                System-Prompt & Verhaltensanweisungen
                            </h3>
                            <p className="text-body-small text-surface-onVariant mt-0.5">
                                Definiert die Rolle, Tonalität, Fakten und Antwortgrenzen des Bots.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleResetPromptToDefault}
                            className="md3-btn-text text-xs text-primary font-bold flex items-center gap-1.5"
                        >
                            <span>↻</span> Auf Stammes-Standard zurücksetzen
                        </button>
                    </div>

                    <div className="relative">
                        <textarea
                            value={config.systemPrompt}
                            onChange={e => setConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
                            rows={16}
                            className="w-full p-4 rounded-2xl bg-surface-containerHigh/40 border border-outlineVariant font-mono text-xs leading-relaxed text-surface-on focus:outline-none focus:border-primary"
                            placeholder="Definiere hier die Persönlichkeit und Regeln des Bots..."
                        />
                    </div>

                    <div className="p-4 rounded-2xl bg-surface-containerHigh/50 border border-outlineVariant/30 flex items-start gap-3">
                        <div className="text-xl">💡</div>
                        <div className="text-body-small text-surface-onVariant space-y-1">
                            <p className="font-bold text-surface-on">Tipp für Verlässlichkeit & White-Labeling:</p>
                            <p>
                                Halte die Anweisungen zu Verbandszugehörigkeit und Grounding strikt, damit der Assistent keine falschen Gruppenstunden-Zeiten oder Verbände halluziniert. Alle Termine und Blogbeiträge werden automatisch live als Kontext übergeben.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: DATENZUGRIFF & RECHTE */}
            {activeTab === 'access' && (
                <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm space-y-6 animate-fade-in">
                    <div>
                        <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                            <ShieldCheckIcon sx={{ fontSize: 20 }} className="text-primary" />
                            Datenzugriff & Informationsquellen
                        </h3>
                        <p className="text-body-small text-surface-onVariant mt-0.5">
                            Lege fest, auf welche Datenbereiche des Stammes der Assistent Zugriff hat, um Besucherfragen präzise zu beantworten.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            { key: 'events', label: 'Termine, Events & Zeltlager', desc: 'Erlaubt dem Bot, über anstehende Aktionen und Lager Auskunft zu geben.', icon: '📅' },
                            { key: 'blog', label: 'Blogbeiträge & Berichte', desc: 'Ermöglicht Antworten zu vergangenen Lagern und aktuellen Stammes-News.', icon: '📰' },
                            { key: 'groups', label: 'Gruppen & Altersstufen', desc: 'Gibt Zeiten, Treffpunkte und Beschreibungen der Gruppenstunden weiter.', icon: '👥' },
                            { key: 'team', label: 'Leitungsteam & Ämter', desc: 'Kennt die Gruppenleiter, Kassenwarte und Ansprechpartner des Stammes.', icon: '🏕️' },
                            { key: 'organization', label: 'Stammesdaten & Impressum', desc: 'Verbandsdaten, Kontaktadresse, Social-Media-Links und Mitgliedsbeiträge.', icon: '📍' },
                            { key: 'knowledge', label: 'Individuelle Wissensdatenbank', desc: 'Greift auf die von dir unten definierten FAQ-Fakten und Sonderregeln zu.', icon: '💡' },
                        ].map(item => (
                            <div
                                key={item.key}
                                className={`p-5 rounded-3xl border transition-all flex items-start justify-between gap-4 ${
                                    config.access[item.key]
                                        ? 'bg-surface-container border-outlineVariant/40 shadow-sm'
                                        : 'bg-surface-containerLowest border-outlineVariant/20 opacity-60'
                                }`}
                            >
                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="text-title-small font-bold text-surface-on">{item.label}</h4>
                                        <p className="text-body-small text-surface-onVariant mt-1 leading-relaxed">
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                    <input
                                        type="checkbox"
                                        checked={config.access[item.key]}
                                        onChange={e => setConfig(prev => ({
                                            ...prev,
                                            access: { ...prev.access, [item.key]: e.target.checked }
                                        }))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* TAB 4: BILDER & AVATARE */}
            {activeTab === 'images' && (
                <div className="space-y-6 animate-fade-in">
                    <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                                <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                    <ImageIcon sx={{ fontSize: 20 }} className="text-primary" />
                                    Avatar-Bilder & Zustände
                                </h3>
                                <p className="text-body-small text-surface-onVariant mt-0.5">
                                    Lade Grafiken für die verschiedenen emotionalen Zustände des Bots hoch oder nutze die Standard-Illustrationen.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleAddExtraImage}
                                className="md3-btn-outlined text-xs flex items-center gap-1.5"
                            >
                                <Add sx={{ fontSize: 16 }} />
                                <span>Eigenen Zustand hinzufügen</span>
                            </button>
                        </div>

                        {/* Standard States Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {DEFAULT_AVATAR_STATES.map(state => {
                                const currentUrl = config.customImages[state.key];
                                const isUploading = uploadingKey === state.key;

                                return (
                                    <div
                                        key={state.key}
                                        className="p-5 rounded-3xl border border-outlineVariant/40 bg-surface-containerHigh/30 flex items-start gap-4"
                                    >
                                        <div className="w-20 h-20 rounded-2xl bg-surface-containerHighest border border-outlineVariant/30 flex items-center justify-center overflow-hidden shrink-0 relative group">
                                            {currentUrl ? (
                                                <img
                                                    src={currentUrl}
                                                    alt={state.label}
                                                    className="w-full h-full object-contain p-1"
                                                />
                                            ) : (
                                                <img
                                                    src={getAssetUrl(`${state.key}.png`)}
                                                    alt={state.label}
                                                    onError={e => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentNode.innerHTML = '<span class="text-2xl">🤖</span>';
                                                    }}
                                                    className="w-full h-full object-contain p-1 opacity-80"
                                                />
                                            )}
                                            {isUploading && (
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                    <RefreshCw className="animate-spin text-white" sx={{ fontSize: 20 }} />
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0 space-y-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="text-title-small font-bold text-surface-on truncate">{state.label}</h4>
                                                    <code className="text-[10px] bg-surface-containerHighest px-2 py-0.5 rounded-full font-mono text-surface-onVariant">
                                                        {state.key}
                                                    </code>
                                                </div>
                                                <p className="text-body-small text-surface-onVariant mt-0.5 text-xs line-clamp-2">
                                                    {state.description}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2 pt-1">
                                                <label className="md3-btn-filled text-xs px-3 py-1.5 cursor-pointer flex items-center gap-1.5">
                                                    <UploadIcon sx={{ fontSize: 14 }} />
                                                    <span>{currentUrl ? 'Ändern' : 'Hochladen'}</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={e => handleImageUpload(state.key, e.target.files[0])}
                                                    />
                                                </label>

                                                {currentUrl && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfig(prev => ({
                                                            ...prev,
                                                            customImages: { ...prev.customImages, [state.key]: '' }
                                                        }))}
                                                        className="text-xs text-error font-bold hover:underline px-2"
                                                    >
                                                        Zurücksetzen
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Extra Custom Images */}
                        {config.extraImages.length > 0 && (
                            <div className="pt-6 border-t border-outlineVariant/30 space-y-4">
                                <h4 className="text-title-small font-bold text-surface-on">Individuelle Zusatzbilder</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {config.extraImages.map((extra, idx) => (
                                        <div key={idx} className="p-4 rounded-2xl border border-outlineVariant/40 bg-surface-containerHigh/20 flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-xl bg-surface-containerHighest border border-outlineVariant/30 flex items-center justify-center overflow-hidden shrink-0">
                                                {extra.url ? (
                                                    <img src={extra.url} alt={extra.name} className="w-full h-full object-contain p-1" />
                                                ) : (
                                                    <span className="text-xl">🖼️</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 space-y-1.5">
                                                <input
                                                    type="text"
                                                    value={extra.name}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setConfig(prev => {
                                                            const arr = [...prev.extraImages];
                                                            arr[idx].name = val;
                                                            return { ...prev, extraImages: arr };
                                                        });
                                                    }}
                                                    placeholder="Bezeichnung (z.B. bob_campfire)"
                                                    className="md3-textfield-outlined text-xs w-full py-1 h-8"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <label className="text-xs text-primary font-bold hover:underline cursor-pointer flex items-center gap-1">
                                                        <span>📁 Hochladen</span>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={e => handleImageUpload(extra.key, e.target.files[0], true, idx)}
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExtraImage(idx)}
                                                        className="text-xs text-error font-bold hover:underline ml-auto"
                                                    >
                                                        Löschen
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB 5: WISSENSDATENBANK & FAQ */}
            {activeTab === 'knowledge' && (
                <div className="p-6 rounded-3xl bg-surface-containerLowest border border-outlineVariant/40 shadow-sm space-y-6 animate-fade-in">
                    <div>
                        <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                            <BookOpenIcon sx={{ fontSize: 20 }} className="text-primary" />
                            Individuelle Wissensdatenbank (FAQ & Fakten)
                        </h3>
                        <p className="text-body-small text-surface-onVariant mt-0.5">
                            Füge spezifisches Stamm-Wissen hinzu (z. B. "Wo kann man die Kluft kaufen?", "Wer hat die Schlüssel zum Pfadiheim?").
                        </p>
                    </div>

                    {/* Add/Edit Form */}
                    <div className="p-5 rounded-3xl bg-surface-containerHigh/40 border border-outlineVariant/30 space-y-4">
                        <h4 className="text-title-small font-bold text-surface-on">
                            {editingFactIndex !== null ? 'Fakt bearbeiten' : 'Neuen Fakt hinzufügen'}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="sm:col-span-2">
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">Titel / Thema</label>
                                <input
                                    type="text"
                                    value={newFact.title}
                                    onChange={e => setNewFact(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="z.B. Kluft & Ausrüstung"
                                    className="md3-textfield-outlined w-full text-xs"
                                />
                            </div>
                            <div>
                                <label className="text-label-small font-bold text-surface-onVariant block mb-1">Kategorie</label>
                                <input
                                    type="text"
                                    value={newFact.category}
                                    onChange={e => setNewFact(prev => ({ ...prev, category: e.target.value }))}
                                    placeholder="z.B. Kleidung, Heim, Lager"
                                    className="md3-textfield-outlined w-full text-xs"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-label-small font-bold text-surface-onVariant block mb-1">Inhalt / Antwort</label>
                            <textarea
                                value={newFact.text}
                                onChange={e => setNewFact(prev => ({ ...prev, text: e.target.value }))}
                                rows={3}
                                placeholder="Genaue Beschreibung oder Fakten, die der Bot wissen soll..."
                                className="md3-textfield-outlined w-full text-xs"
                            />
                        </div>
                        <div className="flex gap-2 justify-end">
                            {editingFactIndex !== null && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingFactIndex(null);
                                        setNewFact({ title: '', category: 'Allgemein', text: '' });
                                    }}
                                    className="md3-btn-outlined text-xs px-4"
                                >
                                    Abbrechen
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleAddOrUpdateFact}
                                disabled={!newFact.text.trim()}
                                className="md3-btn-filled text-xs px-5"
                            >
                                {editingFactIndex !== null ? 'Aktualisieren' : 'Fakt speichern'}
                            </button>
                        </div>
                    </div>

                    {/* List of Knowledge items */}
                    <div className="space-y-3">
                        <h4 className="text-title-small font-bold text-surface-on">
                            Gespeicherte Wissenseinträge ({knowledgeItems.length})
                        </h4>
                        {knowledgeItems.length === 0 ? (
                            <p className="text-xs text-surface-onVariant italic py-4 text-center">
                                Noch keine zusätzlichen Fakten hinterlegt.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 gap-3">
                                {knowledgeItems.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="p-4 rounded-2xl border border-outlineVariant/30 bg-surface-containerHigh/20 flex items-start justify-between gap-4"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-surface-on">{item.title || 'Ohne Titel'}</span>
                                                <span className="text-[10px] bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-full">
                                                    {item.category || 'Allgemein'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-surface-onVariant leading-relaxed">{item.text}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingFactIndex(idx);
                                                    setNewFact(item);
                                                }}
                                                className="p-1.5 hover:text-primary transition-colors text-surface-onVariant"
                                                title="Bearbeiten"
                                            >
                                                <Edit sx={{ fontSize: 16 }} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setKnowledgeItems(prev => prev.filter((_, i) => i !== idx))}
                                                className="p-1.5 hover:text-error transition-colors text-surface-onVariant"
                                                title="Löschen"
                                            >
                                                <Delete sx={{ fontSize: 16 }} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBobSettings;
